import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import {
  nodeMastery,
  type CurriculumNode,
  type Item,
  type MasteryThresholds,
  type RungMastery,
  type ReviewPoint,
} from "@music-app/learning-system";
import type { CardStore } from "@music-app/learning-system";
import { ProgressBar, SkillTreeNode, type ItemDetail } from "@music-app/ui-components";
import { hasLessonForNode } from "@music-app/lessons/runtime";

// ─── types ────────────────────────────────────────────────────────────────────

interface InstrumentProgressSectionProps {
  instrumentLabel: string;
  rungs: RungMastery[];
  nodes: CurriculumNode[];
  items: Item[];
  cards: CardStore;
  thresholds: MasteryThresholds;
  /** Maps rung.id → web drill route for the "Drill this →" link. */
  drillRoutes: Readonly<Record<string, string>>;
  nodeHistories: Map<string, Record<string, ReviewPoint[]>>;
  onNodeExpand: (nodeId: string, expanded: boolean) => Promise<void>;
}

// ─── component ────────────────────────────────────────────────────────────────

export function InstrumentProgressSection({
  instrumentLabel,
  rungs,
  nodes,
  items,
  cards,
  thresholds,
  drillRoutes,
  nodeHistories,
  onNodeExpand,
}: InstrumentProgressSectionProps): ReactNode {
  const [expandedRungId, setExpandedRungId] = useState<string | null>(null);

  const totalMastered = rungs.reduce((s, r) => s + r.mastered, 0);
  const totalItems = rungs.reduce((s, r) => s + r.total, 0);

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  function toggleRung(rungId: string) {
    setExpandedRungId((prev) => (prev === rungId ? null : rungId));
  }

  return (
    <section aria-label={`${instrumentLabel} progress`} className="mb-10">
      {/* header — same pattern as ear-training track headers */}
      <div className="flex items-center gap-4 mb-4 mt-8">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded shrink-0">
          {instrumentLabel.toLowerCase()}
        </h2>
        <hr className="flex-1 border-ink/10" />
        <span className="font-mono text-[10px] text-inkFaded tabular-nums shrink-0">
          {totalMastered}/{totalItems} mastered
        </span>
      </div>

      {/* rung ladder */}
      <div className="flex flex-col gap-2">
        {rungs.map((rm, idx) => {
          const isExpanded = expandedRungId === rm.rung.id;
          const allMastered = rm.total > 0 && rm.mastered === rm.total;
          const rungNodes = rm.rung.nodeIds
            .map((id) => nodeMap.get(id))
            .filter((n): n is CurriculumNode => n !== undefined);
          const drillHref = drillRoutes[rm.rung.id];

          return (
            <div key={rm.rung.id} className="border border-ink/10 bg-paper">
              {/* rung summary row */}
              <button
                type="button"
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-paperWarm/40 transition-colors"
                onClick={() => toggleRung(rm.rung.id)}
                aria-expanded={isExpanded}
                aria-controls={`rung-detail-${rm.rung.id}`}
              >
                {/* rung number badge */}
                <span
                  className="flex-shrink-0 w-6 h-6 rounded-full border border-ink/20 flex items-center justify-center font-mono text-[9px] text-inkFaded"
                  aria-hidden
                >
                  {idx + 1}
                </span>

                {/* label + progress bar */}
                <div className="flex-1 min-w-0">
                  <div className="font-serif italic text-sm text-ink mb-1.5">
                    {rm.rung.label}
                  </div>
                  <ProgressBar
                    total={rm.total}
                    mastered={rm.mastered}
                    practicing={rm.practicing}
                    introduced={rm.introduced}
                    size="sm"
                  />
                </div>

                {/* status */}
                <div className="flex-shrink-0 flex items-center gap-2">
                  {allMastered ? (
                    <span className="font-mono text-[10px] text-success tabular-nums">✓</span>
                  ) : (
                    <span className="font-mono text-[10px] text-inkFaded tabular-nums">
                      {rm.mastered}/{rm.total}
                    </span>
                  )}
                  <span
                    className="font-mono text-[10px] text-inkFaded"
                    aria-hidden
                  >
                    {isExpanded ? "↑" : "↓"}
                  </span>
                </div>
              </button>

              {/* expanded: node cards + drill link */}
              {isExpanded && (
                <div
                  id={`rung-detail-${rm.rung.id}`}
                  className="border-t border-ink/10 p-4 flex flex-col gap-4"
                >
                  {drillHref && (
                    <Link
                      to={drillHref}
                      className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:underline self-start"
                    >
                      Drill this →
                    </Link>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {rungNodes.map((node) => {
                      const m = nodeMastery(node, items, cards, thresholds);
                      const prereqNames = node.prerequisites
                        .map((id) => nodeMap.get(id)?.title)
                        .filter((t): t is string => t !== undefined);
                      const history = nodeHistories.get(node.id);
                      const nodeItems: ItemDetail[] = items
                        .filter((i) => i.nodeId === node.id)
                        .map((i) => {
                          const card = cards[i.id];
                          return {
                            id: i.id,
                            label: i.label,
                            stability: card?.stability ?? 0,
                            due: card?.due ?? null,
                            lapses: card?.lapses ?? 0,
                            state: card?.state ?? "new",
                            reviewHistory: history?.[i.id],
                          };
                        });
                      return (
                        <SkillTreeNode
                          key={node.id}
                          id={node.id}
                          title={node.title}
                          track={`guitar · rung ${idx + 1}`}
                          tier={m.tier}
                          mastered={m.masteredCount}
                          practicing={m.practicingCount}
                          introduced={m.introducedCount}
                          total={m.totalCount}
                          prereqNames={prereqNames}
                          lessonHref={
                            hasLessonForNode(node.id)
                              ? `/learn/${node.id}`
                              : undefined
                          }
                          drillHref={drillHref}
                          items={nodeItems}
                          masteredThreshold={thresholds.mastered}
                          onExpandChange={(expanded) =>
                            void onNodeExpand(node.id, expanded)
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
